import {StrictMode, useEffect, useMemo} from 'react';
import {view, RootLayer} from '@sail/ui';
import {Provider as SailClassicProvider} from '@sail/theme-classic';
import {IntlProvider} from 'react-intl';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {captureException} from '@sentry/browser';

import type {
  AppConfig,
  AppProfile,
} from '@stripe-internal/conversations-assistant';
import {StripeAssistant} from '@stripe-internal/conversations-assistant';
import {MobileAssistant} from '@stripe-internal/mobile-conversations-assistant';

import {DefaultInlineDataComponentRegistry} from '@stripe-internal/mse-inline-data';
import type {Config} from '@stripe-internal/conversations-shared-dependencies';
import {
  ObservabilityProvider,
  useObservability,
} from './Providers/ObservabilityProvider';
import {buildConfig} from './buildExtractedConfig';
import {supportFetch} from './helpers/supportFetch';
import {useConvUITheme} from './Providers/AppThemeProvider';

type Props = {
  locale?: string;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
  config: Config;
};

function AppByEntryPoint({
  config,
  profile,
}: {
  config: AppConfig;
  profile: AppProfile;
}) {
  const observability = useObservability();

  useEffect(() => {
    if (!profile.featureFlags.useExtractedConversationsAssistant) {
      captureException('Running extracted app without feature flag enabled', {
        extra: {
          entryPoint: config.entryPoint,
        },
      });
    }
  }, [config, profile]);

  const mobileEntryPoints = [
    'mobile_ios',
    'mobile_android',
    'react_native_mobile_ios',
    'react_native_mobile_android',
    'mobile_link_ios',
    'mobile_link_android',
  ];

  if (mobileEntryPoints.includes(config.entryPoint)) {
    return (
      <MobileAssistant
        config={config}
        profile={profile}
        observability={observability}
      />
    );
  }

  return (
    <SailClassicProvider>
      <RootLayer>
        <view.div css={{height: '100vh'}}>
          <ThemedStripeAssistant
            config={config}
            profile={profile}
            observability={observability}
          />
        </view.div>
      </RootLayer>
    </SailClassicProvider>
  );
}

function ThemedStripeAssistant({
  config,
  profile,
  observability,
}: {
  config: AppConfig;
  profile: AppProfile;
  observability: ReturnType<typeof useObservability>;
}) {
  const {theme, overrides} = useConvUITheme(config);

  return (
    <StripeAssistant
      config={config}
      profile={profile}
      clientFetch={supportFetch}
      inlineDataComponents={DefaultInlineDataComponentRegistry}
      observability={observability}
      theme={theme}
      themeOverrides={overrides}
      withLiveChat
    />
  );
}

export function ExtractedApp({locale = 'en-US', messages, config}: Props) {
  const {config: appConfig, profile} = useMemo(() => {
    return buildConfig(config, locale);
  }, [config, locale]);

  return (
    <IntlProvider locale={locale} messages={messages} onError={() => {}}>
      <StrictMode>
        <ObservabilityProvider>
          <AppByEntryPoint config={appConfig} profile={profile} />
        </ObservabilityProvider>
      </StrictMode>
    </IntlProvider>
  );
}
